import { Logomark } from '@axioma/ui';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  Columns3,
  FileText,
  GanttChartSquare,
  Gavel,
  Inbox,
  LayoutDashboard,
  Library,
  Scale,
  TrendingUp,
} from 'lucide-react';
import { NavLink } from 'react-router';

import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';
import { useUIStore } from '../stores/ui.js';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  hint?: string; // ex.: "g a"
  badgeQueryKey?: 'inbox';
}

const NAV: ReadonlyArray<NavItem> = [
  { to: '/', label: 'Visão geral', icon: <LayoutDashboard size={16} /> },
  { to: '/inbox', label: 'Inbox', icon: <Inbox size={16} />, hint: 'g i', badgeQueryKey: 'inbox' },
  { to: '/agenda', label: 'Agenda crítica', icon: <CalendarDays size={16} />, hint: 'g a' },
  { to: '/kanban', label: 'Kanban', icon: <Columns3 size={16} />, hint: 'g k' },
  { to: '/carteira', label: 'Carteira', icon: <Library size={16} />, hint: 'g c' },
  { to: '/risco', label: 'Painel de risco', icon: <TrendingUp size={16} />, hint: 'g r' },
];

const SECTIONS: ReadonlyArray<{ label: string; items: ReadonlyArray<NavItem> }> = [
  {
    label: 'Vigília',
    items: [{ to: '/processos', label: 'Processos', icon: <Gavel size={16} />, hint: 'g p' }],
  },
  {
    label: 'Estúdio',
    items: [
      { to: '/matters', label: 'Matters', icon: <Scale size={16} /> },
      { to: '/documentos', label: 'Documentos', icon: <FileText size={16} /> },
      { to: '/pipelines', label: 'Pipelines IA', icon: <GanttChartSquare size={16} /> },
    ],
  },
];

export function Sidebar(): JSX.Element {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const tenant = useAuthStore((s) => s.currentTenantId);

  const inboxQuery = useQuery({
    queryKey: ['vigilia', 'inbox', tenant, 'badge'],
    queryFn: async () => {
      const { data } = await api.get<{ unreadCount: number }>('/vigilia/inbox', {
        params: { pageSize: 1 },
      });
      return data.unreadCount;
    },
    enabled: Boolean(tenant),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const badges: Record<string, number> = { inbox: inboxQuery.data ?? 0 };

  return (
    <aside
      className={`flex flex-col border-r border-ax-line bg-ax-graphite transition-all duration-ax-base ${
        collapsed ? 'w-[60px]' : 'w-[240px]'
      }`}
    >
      <div className="flex h-[60px] items-center gap-2.5 border-b border-ax-line px-4 py-4">
        <Logomark size={22} />
        {!collapsed && (
          <span className="ax-wordmark text-base uppercase tracking-tighter text-ax-text-primary">
            A<span className="ax-gradient-text">X</span>IOMA
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5 px-2">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavItemLink
                item={item}
                collapsed={collapsed}
                badge={item.badgeQueryKey ? badges[item.badgeQueryKey] : undefined}
              />
            </li>
          ))}
        </ul>

        {SECTIONS.map((section) => (
          <div key={section.label} className="mt-5">
            {!collapsed && (
              <div className="mb-2 px-3">
                <span className="ax-eyebrow text-ax-text-tertiary">{section.label}</span>
              </div>
            )}
            <ul className="space-y-0.5 px-2">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavItemLink item={item} collapsed={collapsed} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-ax-line px-3 py-3">
        {!collapsed && (
          <p className="font-mono text-[10px] leading-relaxed text-ax-text-tertiary">
            <span className="ax-editorial text-xs italic text-ax-text-secondary">
              Antes da prova, o axioma.
            </span>
          </p>
        )}
      </div>
    </aside>
  );
}

function NavItemLink({
  item,
  collapsed,
  badge,
}: {
  item: NavItem;
  collapsed: boolean;
  badge?: number;
}): JSX.Element {
  const showBadge = typeof badge === 'number' && badge > 0;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        [
          'group flex items-center gap-2.5 rounded-ax-md px-2.5 py-1.5',
          'font-display text-[13px] font-medium tracking-tight',
          'transition-colors duration-ax-fast',
          isActive
            ? 'bg-ax-night text-ax-text-primary'
            : 'hover:bg-ax-night/60 text-ax-text-secondary hover:text-ax-text-primary',
        ].join(' ')
      }
      title={collapsed ? item.label : undefined}
    >
      <span className="relative flex items-center justify-center text-ax-text-tertiary transition-colors group-hover:text-ax-aurora">
        {item.icon}
        {collapsed && showBadge && (
          <span className="absolute -right-1.5 -top-1.5 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-ax-aurora px-1 font-mono text-[9px] font-bold text-ax-obsidian">
            {badge > 99 ? '99+' : String(badge)}
          </span>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {showBadge && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-ax-aurora px-1.5 font-mono text-[10px] font-bold text-ax-obsidian">
              {badge > 99 ? '99+' : String(badge)}
            </span>
          )}
          {item.hint && !showBadge && (
            <span className="font-mono text-[10px] text-ax-text-tertiary opacity-0 transition-opacity group-hover:opacity-100">
              {item.hint}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
